import RdnaSmartTable from '@ringdna/common/src/components/RdnaTable/RdnaSmartTable'
import React, { useCallback, useMemo, useState } from 'react'
import { Column } from 'react-table'
import { SFUser } from '../../../../store/api/users-api'
import { COLUMN_WIDTH, getPaginationProps, License, title } from '../utils'
import styled from 'styled-components'
import RdnaCheckbox from '@ringdna/common/src/components/RdnaFormElements/RdnaCheckbox'
import AvailableLicenses from '../AvailableLicenses'
import { flags, isFlagged } from '@ringdna/common/src/utils/feature-flag/featureFlag'

type Props = {
  userList: SFUser[]
  setUserList: (list: SFUser[]) => void
  remainingLicenses: { [key: string]: number }
}

export default function AssignLicenses({ userList, setUserList, remainingLicenses }: Props) {
  const [page, setPage] = useState(1)
  const [availableLicenses, setAvailableLicenses] = useState<{ [key: string]: number }>({
    dialerLicense: remainingLicenses[License.INTELLIGENT_DIALER],
    caiLicense: remainingLicenses[License.CONVERSATION_AI],
    momentsLicense: remainingLicenses[License.MOMENTS],
    gsLicense: remainingLicenses[License.GUIDED_SELLING],
    revenueAILicense: remainingLicenses[License.REVENUE_AI],
    calendarLicense: remainingLicenses[License.CALENDAR],
    adminLicense: remainingLicenses[License.ADMIN]
  })

  const onChangeHeaderCheckbox = useCallback(
    (key: string, checked: boolean) => {
      let available = availableLicenses[key]
      setUserList(
        userList.map((user: any) => {
          if (checked && !user[key]) {
            available = available - 1
          } else if (!checked && user[key]) {
            available = available + 1
          }
          available >= 0 && setAvailableLicenses({ ...availableLicenses, [key]: available })
          return available >= 0 ? { ...user, [key]: checked } : user
        })
      )
    },
    [availableLicenses, setUserList, userList]
  )

  const onChangeCheckbox = useCallback(
    (key: string, checked: boolean, salesforceId: string) => {
      setUserList(userList.map(user => (user.salesforceId === salesforceId ? { ...user, [key]: checked } : user)))
      setAvailableLicenses({ ...availableLicenses, [key]: availableLicenses[key] - (checked ? 1 : -1) })
    },
    [availableLicenses, setUserList, userList]
  )

  const columns = useMemo<Column<SFUser>[]>(() => {
    const columns = [{ Header: 'Name', accessor: 'name', disableSortBy: true, width: COLUMN_WIDTH } as Column<SFUser>]
    Object.keys(availableLicenses).forEach(license => {
      if (!isFlagged(flags.v299_REV_2816) && license === 'revenueAILicense') {
        return
      }
      columns.push({
        id: license,
        accessor: user => user,
        width: COLUMN_WIDTH,
        disableSortBy: true,
        Header: function headerCell(data) {
          return (
            <RdnaCheckbox
              className={'header-setting'}
              onChange={(checked: boolean) => onChangeHeaderCheckbox(license, checked)}
              value={
                data.data.every((user: any) => user[license]) ||
                (availableLicenses[license] <= 0 && data.data.some((user: any) => user[license]))
              }
              label={title(license)}
              labelPlacement={'top'}
            />
          )
        },
        Cell: (data: { value: any }) => (
          <RdnaCheckbox
            className={'checkbox-setting'}
            onChange={(checked: boolean) => onChangeCheckbox(license, checked, data.value.salesforceId)}
            value={data.value[license]}
            disabled={!data.value[license] && availableLicenses[license] <= 0}
          />
        )
      } as Column<SFUser>)
    })
    return columns
  }, [availableLicenses, onChangeCheckbox, onChangeHeaderCheckbox])

  return (
    <Container>
      <AvailableLicenses availableLicenses={availableLicenses} />
      <RdnaSmartTable
        columns={columns}
        data={userList}
        onPagination={current => setPage(current)}
        initialTableState={{ pageIndex: page - 1 }}
        {...getPaginationProps(userList, userList.length)}
      />
    </Container>
  )
}

const Container = styled.div`
  [role='columnheader']:nth-child(n + 2) {
    height: auto;
    align-self: flex-end;
  }
  .header-setting label {
    span:last-child {
      text-align: center;
      white-space: normal;
      word-break: break-word;
    }
  }
  .checkbox-setting {
    align-content: center;
    flex-wrap: wrap;
    margin-left: ${({ theme }) => theme.spacing * 4.5}px;
  }
`
